import { Rows3 } from 'lucide-react';

import { useDensity, type Density } from '@/components/DensityProvider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Topbar-Switch fuer Layout-Dichte (Compact vs Comfortable).
 *
 * Setzt `--density-y` auf `<html>` und persistiert die Wahl in localStorage
 * via `DensityProvider`. Wirkung primaer im Card-Primitive (Padding skaliert
 * mit `var(--density-y)`).
 */
export function DensityToggle(): JSX.Element {
  const { density, setDensity } = useDensity();

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Layout-Dichte wechseln"
                className="h-9 w-9"
              >
                <Rows3 className="h-4 w-4" />
                <span className="sr-only">Layout-Dichte wechseln</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Dichte</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel>Layout-Dichte</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DensityItem current={density} value="comfortable" onSelect={setDensity}>
            Komfortabel
          </DensityItem>
          <DensityItem current={density} value="compact" onSelect={setDensity}>
            Kompakt
          </DensityItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}

function DensityItem({
  current,
  value,
  onSelect,
  children,
}: {
  current: Density;
  value: Density;
  onSelect: (d: Density) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <DropdownMenuCheckboxItem
      checked={current === value}
      onCheckedChange={() => onSelect(value)}
      className="gap-2"
    >
      {children}
    </DropdownMenuCheckboxItem>
  );
}
